import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { SimCardType } from '@app/sales/models/simcard.model';

export const SimCardValidationErrors = {
  required: 'SIM-nummer må fylles ut.',
  pattern: 'SIM-nummer har feil format.',
  simCardRepeated: 'SIM-kort er allerede i bruk.',
  simCardInUse: 'SIM-kort er allerede i bruk.',
  simCardTypeInvalidVoice: 'SIM-kort er av feil type. Bruk SIM-kort for voice-abonnement.',
  simCardTypeInvalidMbb: 'SIM-kort er av feil type. Bruk SIM-kort for mobilt bredbånd.',
  apiException: 'SIM-kort er ikke gyldig',
  minLength: 'SIM-nummer må være minst 19 siffer',
  maxLength: 'SIM-nummer kan ikke være lenger enn 20 siffer',
  invalidSim: 'SIM-nummer er ikke gyldig',
};

export const GtinValidationErrors = {
  invalidGtin: 'Ikke gyldig GTIN',
  invalidCellPhoneProductType: 'GTIN tilhører en mobiltelefon.',
  invalidAccessoryProductType: 'GTIN tilhører et tilbehør.',
};

export const ImeiValidationErrors = {
  imeiInvalid: 'IMEI-nummer er ikke gyldig',
  minLength: 'IMEI må være minst 15 siffer',
  imeiDuplicateInvalid: 'Duplikat IMEI',
};

const SsnValidationErrors = {
  invalidSsn: 'Fødselsnummer er ikke gyldig.',
  invalidLength: 'Fødselsnummer må være 11 siffer.',
  isDNumber: 'Kan ikke være et D-nummer.',
};

const EmailValidationErrors = {
  maxLength: 'E-postadressen er for lang',
  invalidEmail: 'Ugyldig e-postadresse',
};

const RouterNameValidationErrors = {
  minLength: 'Navn på ruter må være minst 3 tegn',
  maxLength: 'Navn på ruter kan ikke være lenger enn 50 tegn',
  invalidRouterName: 'Ugyldig navn på ruter',
};

const MsisdnValidationErrors = {
  pattern: 'Nummeret må starte med 4 eller 9, og være 8 siffer',
  invalidMsisdn: 'Ugyldig nummer',
};

const NameValidationErrors = {
  invalidFullName: 'Navn må inneholde fornavn og etternavn',
  illegalCharacter: 'Navn kan ikke inneholde spesialtegn',
  invalidName: 'Ugyldig navn',
};

@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  static getSimCardErrorTexts(errors: ValidationErrors, simCardType: SimCardType): string {
    if (!errors) {
      return '';
    }

    if (errors.pattern) {
      return SimCardValidationErrors.pattern;
    }
    if (errors.simCardRepeated || errors.simCardInUse) {
      return SimCardValidationErrors.simCardRepeated;
    }
    if (!errors.simCardInUse && errors.simCardTypeInvalid) {
      return simCardType === SimCardType.Voice
        ? SimCardValidationErrors.simCardTypeInvalidVoice
        : SimCardValidationErrors.simCardTypeInvalidMbb;
    }
    if (errors.apiException) {
      return SimCardValidationErrors.apiException;
    }
    if (errors.minlength) {
      return SimCardValidationErrors.minLength;
    }
    if (errors.maxlength) {
      return SimCardValidationErrors.maxLength;
    }

    return SimCardValidationErrors.invalidSim;
  }

  static getGtinErrorText(errors: ValidationErrors): string {
    if (!errors) {
      return '';
    }

    if (errors.invalidCellPhoneProductType) {
      return GtinValidationErrors.invalidCellPhoneProductType;
    }

    if (errors.invalidAccessoryProductType) {
      return GtinValidationErrors.invalidAccessoryProductType;
    }

    return GtinValidationErrors.invalidGtin;
  }

  static getImeiErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.isImeiUsedBefore) return '';

    if (!!errors.minlength) return ImeiValidationErrors.minLength;

    if (!!errors.imeiDuplicateInvalid) return ImeiValidationErrors.imeiDuplicateInvalid;

    return ImeiValidationErrors.imeiInvalid;
  }

  static getSsnErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.minlength || !!errors.maxlength) return SsnValidationErrors.invalidLength;

    if (!!errors.isDNumber) return SsnValidationErrors.isDNumber;

    return SsnValidationErrors.invalidSsn;
  }

  static getEmailErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.maxlength) return EmailValidationErrors.maxLength;

    return EmailValidationErrors.invalidEmail;
  }

  static getRouterNameErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.minlength) return RouterNameValidationErrors.minLength;

    if (!!errors.maxlength) return RouterNameValidationErrors.maxLength;

    return RouterNameValidationErrors.invalidRouterName;
  }

  static getMsisdnErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.pattern) return MsisdnValidationErrors.pattern;

    return MsisdnValidationErrors.invalidMsisdn;
  }

  static getNameErrorText(errors: ValidationErrors): string {
    if (!errors) return '';

    if (!!errors.invalidFullName) return NameValidationErrors.invalidFullName;

    if (!!errors.illegalCharacter) return NameValidationErrors.illegalCharacter;

    return NameValidationErrors.invalidName;
  }
}
